import { DateTimePicker } from '../DateTimePicker';
import { Options, Select } from '@/components/Controls/Select';
import { TFunction } from 'i18next';
import { NotFound } from '@/components/Message/NotFound';
import dayjs, { Dayjs } from 'dayjs';

type VehicleCheckFilterProps = {
  t: TFunction<undefined>;
  isLoadingAllGroups?: boolean;
  isLoadingAllCompanies?: boolean;
  isLoadingAllBranches?: boolean;
  humanModerationStatusOptions?: Options[];
  reportValidatedOptions?: Options[];
  groupOptions?: Options[];
  companyOptions?: Options[];
  branchOptions?: Options[];
  displayGroupFilter?: boolean;
  displayCompanyFilter?: boolean;
  displayBranchFilter?: boolean;
  humanModerationStatus?: string;
  reportValidated?: string;
  groupId?: string;
  companyId?: string;
  branchId?: string;
  startDate?: string;
  endDate?: string;
  searchStartDate?: (value: Dayjs) => void;
  searchEndDate?: (value: Dayjs) => void;
  searchByHumanModerationStatus?: (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => void;
  searchByReportValidated?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  searchByGroup?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  searchByCompany?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  searchByBranch?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
};

const VehicleCheckFilter = ({
  t,
  isLoadingAllGroups,
  isLoadingAllCompanies,
  isLoadingAllBranches,
  humanModerationStatusOptions,
  reportValidatedOptions,
  groupOptions,
  companyOptions,
  branchOptions,
  displayGroupFilter,
  displayBranchFilter,
  displayCompanyFilter,
  humanModerationStatus,
  reportValidated,
  groupId,
  companyId,
  branchId,
  startDate,
  endDate,
  searchStartDate,
  searchEndDate,
  searchByHumanModerationStatus,
  searchByReportValidated,
  searchByGroup,
  searchByCompany,
  searchByBranch,
}: VehicleCheckFilterProps) => {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-row gap-3 select-datepicker-container">
        <div className="flex flex-row gap-3">
          <label className="text-gray-600 text-sm self-center">
            {t('common:createdBetween_feminine')}
          </label>
          <DateTimePicker
            useTime={false}
            value={startDate ? dayjs(startDate) : null}
            onChange={searchStartDate}
            disablePast={false}
            numberOfAllowedFutureDays={0}
            maxDate={endDate ? dayjs(endDate) : null}
          />
        </div>
        <div className="flex flex-row gap-3">
          <label className="text-gray-600 text-sm self-center">
            {t('common:and').toLowerCase()}
          </label>
          <DateTimePicker
            useTime={false}
            value={endDate ? dayjs(endDate) : null}
            onChange={searchEndDate}
            disablePast={false}
            numberOfAllowedFutureDays={0}
            minDate={startDate ? dayjs(startDate) : null}
          />
        </div>
      </div>
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <div className="flex flex-1 text-gray-600 text-sm">
            {`${t('common:moderationStatus')} :`}
          </div>
          <div className="w-[270px]">
            <Select
              selectClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500"
              placeholder={t('common:allModerations')}
              options={humanModerationStatusOptions}
              value={humanModerationStatus || ''}
              onChange={searchByHumanModerationStatus}
            />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex flex-1 text-gray-600 text-sm">
            {`${t('common:validationStatus')} :`}
          </div>
          <div className="w-[270px]">
            <Select
              selectClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500"
              placeholder={t('common:allValidations')}
              options={reportValidatedOptions}
              value={reportValidated}
              onChange={searchByReportValidated}
            />
          </div>
        </div>
        {displayGroupFilter && (
          <div className="flex items-center gap-3">
            <div className="flex flex-1 text-gray-600 text-sm">
              {`${t('common:linkedToGroup')} :`}
            </div>
            <div className="w-[270px]">
              {isLoadingAllGroups ? (
                <Select
                  selectClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500"
                  placeholder={t('common:allGroups')}
                  options={[]}
                />
              ) : (
                <Select
                  selectClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500"
                  placeholder={t('common:allGroups')}
                  options={groupOptions}
                  value={groupId || ''}
                  onChange={searchByGroup}
                />
              )}
            </div>
          </div>
        )}
        {displayCompanyFilter && (
          <div className="flex items-center gap-3">
            <div className="flex flex-1 text-gray-600 text-sm">
              {`${t('common:linkedToCompany')} :`}
            </div>
            <div className="w-[270px]">
              {isLoadingAllCompanies ? (
                <Select
                  selectClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500"
                  placeholder={t('common:allCompanies')}
                  options={[]}
                />
              ) : groupId && companyOptions.length === 0 ? (
                <NotFound
                  containerClassName="justify-start gap-1.5"
                  iconClassName="-mt-1.5 text-lg"
                  textClassName="text-2sm"
                  text={t('common:noCompanyLinkedToThisGroup')}
                />
              ) : companyOptions.length === 0 ? (
                <NotFound
                  containerClassName="justify-start gap-1.5"
                  iconClassName="-mt-1.5 text-lg"
                  textClassName="text-2sm"
                  text={t('common:youAreNotLinkedToAnyCompany')}
                />
              ) : (
                <Select
                  selectClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500"
                  placeholder={t('common:allCompanies')}
                  options={companyOptions}
                  value={companyId || ''}
                  onChange={searchByCompany}
                />
              )}
            </div>
          </div>
        )}
        {displayBranchFilter && (
          <div className="flex items-center gap-3">
            <div className="flex flex-1 text-gray-600 text-sm">
              {`${t('common:linkedToBranch')} :`}
            </div>
            <div className="w-[270px]">
              {isLoadingAllBranches ? (
                <Select
                  selectClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500"
                  placeholder={t('common:allBranches')}
                  options={[]}
                />
              ) : companyId && branchOptions.length === 0 ? (
                <NotFound
                  containerClassName="justify-start gap-1.5"
                  iconClassName="-mt-1.5 text-lg"
                  textClassName="text-2sm"
                  text={t('common:noBranchLinkedToThisCompany')}
                />
              ) : groupId && branchOptions.length === 0 ? (
                <NotFound
                  containerClassName="justify-start gap-1.5"
                  iconClassName="-mt-1.5 text-lg"
                  textClassName="text-2sm"
                  text={t('common:noBranchLinkedToThisGroup')}
                />
              ) : branchOptions.length === 0 ? (
                <NotFound
                  containerClassName="justify-start gap-1.5"
                  iconClassName="-mt-1.5 text-lg"
                  textClassName="text-2sm"
                  text={t('common:youAreNotLinkedToAnyBranch')}
                />
              ) : (
                <Select
                  selectClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500"
                  placeholder={t('common:allBranches')}
                  options={branchOptions}
                  value={branchId || ''}
                  onChange={searchByBranch}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VehicleCheckFilter;
